import type { Item, StockStateKey } from '@eshop/types/catalogue.types.ts';
import {
  DEFAULT_CATALOG_FLAGS,
  DEFAULT_CATALOG_LABELS,
  type ResolvedCatalogFlags,
  type ResolvedCatalogLabels,
} from '@eshop/hooks/useSiteLabels.ts';

export interface StockPresentation {
  canBuy: boolean;
  state: StockStateKey | null;
  badge: { text: string; color: string } | null;
  message: string | null;
  ctaLabel: string | null;
  policyEnforced: boolean;
}

type StockCopy = {
  label?: string | null;
  color: string;
  message?: string | null;
  ctaLabel?: string | null;
};

const definedString = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const createStockStateCopy = (
  labels: ResolvedCatalogLabels,
  flags: ResolvedCatalogFlags,
): Record<StockStateKey, StockCopy> => ({
  in_stock: { label: labels.inStockBadge, color: 'green' },
  backorder: {
    label: flags.hideBackorderLabel ? null : labels.backorderBadge,
    color: 'gold',
    message: definedString(labels.backorderMessage),
  },
  out_of_stock: {
    label: labels.outOfStockBadge,
    color: 'red',
    message: definedString(labels.outOfStockMessage),
    ctaLabel: definedString(labels.notifyMeCta),
  },
  unknown: {
    label: labels.unknownBadge,
    color: 'default',
    message: definedString(labels.unknownMessage),
    ctaLabel: definedString(labels.notifyMeCta),
  },
});

export const DEFAULT_BLOCKED_MESSAGE = DEFAULT_CATALOG_LABELS.blockedMessage;
export const DEFAULT_OVERSALE_MESSAGE = DEFAULT_CATALOG_LABELS.oversaleMessage;
export const DEFAULT_NOTIFY_ME_CTA = DEFAULT_CATALOG_LABELS.notifyMeCta;

export const deriveStockPresentation = (
  candidate: Item | null,
  fallback?: Item | null,
  options?: {
    policyEnforced?: boolean;
    isBlockedByPolicy?: boolean;
    copy?: {
      labels?: ResolvedCatalogLabels;
      flags?: ResolvedCatalogFlags;
    };
  },
): StockPresentation => {
  const policyEnforced = Boolean(options?.policyEnforced);
  const isBlockedByPolicy = Boolean(options?.isBlockedByPolicy && policyEnforced);
  const labels = options?.copy?.labels ?? DEFAULT_CATALOG_LABELS;
  const flags = options?.copy?.flags ?? DEFAULT_CATALOG_FLAGS;
  const stockCopy = createStockStateCopy(labels, flags);
  const reference = candidate ?? fallback ?? null;
  const resolvedState = (candidate?.stock_state ?? fallback?.stock_state ?? null) as StockStateKey | null;
  const availabilitySource = candidate?.can_buy ?? fallback?.can_buy ?? reference?.disponibilidad;
  let canBuy = availabilitySource !== false;

  if (policyEnforced) {
    if (resolvedState === 'out_of_stock' || isBlockedByPolicy) {
      canBuy = false;
    }
  } else if (resolvedState === 'backorder' || resolvedState === 'out_of_stock') {
    canBuy = availabilitySource !== false;
  }

  if (!resolvedState) {
    return {
      canBuy,
      state: null,
      badge: null,
      message: canBuy
        ? null
        : policyEnforced
          ? definedString(labels.blockedMessage) ?? DEFAULT_BLOCKED_MESSAGE
          : definedString(labels.oversaleMessage) ?? DEFAULT_OVERSALE_MESSAGE,
      ctaLabel: canBuy ? null : definedString(labels.notifyMeCta) ?? DEFAULT_NOTIFY_ME_CTA,
      policyEnforced,
    } satisfies StockPresentation;
  }

  const copy = stockCopy[resolvedState] ?? stockCopy.unknown;
  const fallbackBlockedMessage = definedString(labels.blockedMessage) ?? DEFAULT_BLOCKED_MESSAGE;
  const fallbackOversaleMessage = definedString(labels.oversaleMessage) ?? DEFAULT_OVERSALE_MESSAGE;
  const fallbackNotifyMe = definedString(labels.notifyMeCta) ?? DEFAULT_NOTIFY_ME_CTA;
  let message =
    definedString(copy.message)
    ?? (canBuy ? null : policyEnforced ? fallbackBlockedMessage : fallbackOversaleMessage);
  let ctaLabel = definedString(copy.ctaLabel) ?? (canBuy ? null : fallbackNotifyMe);

  if (!policyEnforced && canBuy && (resolvedState === 'backorder' || resolvedState === 'out_of_stock')) {
    message = fallbackOversaleMessage;
    ctaLabel = fallbackNotifyMe;
  }

  if (policyEnforced && !canBuy) {
    message = message ?? fallbackBlockedMessage;
    ctaLabel = ctaLabel ?? fallbackNotifyMe;
  }

  return {
    canBuy,
    state: resolvedState,
    badge: copy.label ? { text: copy.label, color: copy.color } : null,
    message,
    ctaLabel,
    policyEnforced,
  } satisfies StockPresentation;
};
